import React, { useState } from 'react';
import { Globe, Check } from 'lucide-react';
import { Button } from './Button';
import { Tooltip, TooltipTrigger, TooltipContent } from './Tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from './DropdownMenu';
import { useLanguage } from '../helpers/useLocalization';
import { updateUserLanguage } from '../endpoints/user-settings/language_POST.schema';
import { SupportedLanguage } from '../locales';
import styles from './LanguageSelector.module.css';

interface LanguageSelectorProps {
  className?: string;
  compact?: boolean;
  showLabel?: boolean;
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  className,
  compact = false,
  showLabel = true,
}) => {
  const { language, setLanguage, supportedLanguages } = useLanguage();
  const [isUpdating, setIsUpdating] = useState(false);

  const currentLang = supportedLanguages.find((l) => l.code === language);

  const handleLanguageChange = async (langCode: SupportedLanguage) => {
    if (langCode === language) return;

    setIsUpdating(true);
    try {
      // Update locally first for immediate feedback
      setLanguage(langCode);

      // Save to server
      await updateUserLanguage({ language: langCode });
    } catch (error) {
      console.error('Failed to update language preference:', error);
      // Revert on error
      setLanguage(language);
    } finally {
      setIsUpdating(false);
    }
  };

  if (compact) {
    return (
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                className={`${styles.compactTrigger} ${className || ''}`}
                disabled={isUpdating}
              >
                <Globe size={16} />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>Language</TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="end" className={styles.dropdown}>
          {supportedLanguages.map((lang) => (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className={styles.menuItem}
            >
              <span className={styles.langName}>{lang.nativeName}</span>
              {language === lang.code && <Check size={14} className={styles.checkIcon} />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={`${styles.trigger} ${className || ''}`}
          disabled={isUpdating}
        >
          <Globe size={16} />
          {showLabel && (
            <span className={styles.currentLang}>{currentLang?.nativeName || language}</span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={styles.dropdown}>
        <DropdownMenuLabel className={styles.dropdownLabel}>Select Language</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {supportedLanguages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className={`${styles.menuItem} ${lang.dir === 'rtl' ? styles.rtl : ''}`}
          >
            <div className={styles.langInfo}>
              <span className={styles.langNative}>{lang.nativeName}</span>
              <span className={styles.langEnglish}>{lang.name}</span>
            </div>
            {language === lang.code && <Check size={14} className={styles.checkIcon} />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Quick toggle button for switching between last two languages
export const LanguageQuickToggle: React.FC<{ className?: string }> = ({ className }) => {
  const { language, setLanguage, supportedLanguages } = useLanguage();
  const [lastLanguage, setLastLanguage] = useState<SupportedLanguage>('en');

  const handleToggle = async () => {
    const newLang = language === 'en' ? lastLanguage : 'en';
    if (language !== 'en') {
      setLastLanguage(language);
    }

    setLanguage(newLang);
    try {
      await updateUserLanguage({ language: newLang });
    } catch (error) {
      console.error('Failed to update language:', error);
    }
  };

  const currentLang = supportedLanguages.find((l) => l.code === language);

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleToggle}
      className={`${styles.quickToggle} ${className || ''}`}
      title="Toggle language"
    >
      <Globe size={14} />
      <span>{currentLang?.code.toUpperCase() || 'EN'}</span>
    </Button>
  );
};
